import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Ban, Check, ExternalLink, Loader2 } from "lucide-react";
import { drw, errText } from "./api";

/** PLAN-COMPLETE §12 — a mark that is not linked cannot be completed from the plan. Tapping it
 *  says why and offers the three discrepancy fixes: reassign the room, exclude the takeoff, or
 *  create the room in the FRS (after which the mark re-matches itself, no re-upload).
 */
const WHY = {
  unknown_room: (s) => `Room ${s.room_number || "—"} is not on the FRS.`,
  ambiguous_room: (s) => `Room Number ${s.room_number} names two rooms and the FRS has ${s.scope_code} in both.`,
  scope_not_in_room: (s) => `The FRS has no ${s.scope_code} in room ${s.room_number}.`,
  unknown_scope: (s) => `Subject “${s.subject}” is not a scope on this job.`,
  unknown_product: (s) => `“${s.label}” does not name an FRS product.`,
  no_room: () => "This markup carries no Room Number in Bluebeam.",
  excluded: () => "This takeoff was ruled wrong and is excluded from completion.",
};

export default function UnlinkedMark({ mark, setId, projectId, onClose, onChanged }) {
  const st = mark.mark_state || {};
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const why = st.why || "no_room";
  const kind = ["unknown_room", "ambiguous_room", "scope_not_in_room"].includes(why) ? "room"
    : why === "unknown_scope" ? "subject" : why === "unknown_product" ? "label" : "no_room";
  const value = kind === "room" ? (st.room_number || "")
    : kind === "subject" ? (st.subject || "")
      : kind === "label" ? (st.label || "") : `${st.subject || ""} · ${st.label || ""}`;

  const load = useCallback(async () => {
    if (!setId) return;
    try {
      const { data } = await drw.get(`/sets/${setId}/link-review`);
      setRows(data.rows || []);
    } catch { setRows([]); }
  }, [setId]);
  useEffect(() => { load(); }, [load]);
  const row = (rows || []).find((r) => r.kind === kind && r.value === value) || null;

  const act = async (fn, done) => {
    setBusy(true);
    try { await fn(); toast.success(done); onChanged?.(); onClose?.(); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-white border-2 border-rose-300 rounded-xl shadow-xl p-3 w-full sm:w-80 space-y-2 text-sm"
         data-testid="unlinked-mark" data-why={why}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-rose-900">Not linked — it cannot be completed here</div>
          <div className="text-[12px] text-neutral-700 mt-0.5" data-testid="unlinked-why">{(WHY[why] || WHY.no_room)(st)}</div>
          <div className="text-[11px] text-neutral-500 mt-1 font-mono truncate">{st.subject} · {st.label}</div>
        </div>
        <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="unlinked-close">✕</button>
      </div>
      {kind === "no_room" ? (
        <div className="text-[12px] text-neutral-600" data-testid="unlinked-bluebeam">
          Add the room number in Bluebeam and upload the set again — nothing here can fix it.
        </div>
      ) : rows === null ? (
        <div className="text-[12px] text-neutral-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> reading the FRS…</div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy}
                    className="h-9 border rounded-md px-1 flex-1 min-w-0 text-xs" data-testid="unlinked-pick">
              <option value="">{kind === "room" ? "— reassign to this FRS room —" : "— which is it? —"}</option>
              {(row?.choices || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button type="button" disabled={!pick || busy}
                    onClick={() => act(() => drw.post(`/sets/${setId}/link-fix`, { kind, value, target: pick }),
                                      `${value} → ${pick} · every mark carrying it re-read`)}
                    className="h-9 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="unlinked-reassign"><Check className="w-3.5 h-3.5" /> Fix all {row?.marks || ""}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {row?.frs_room_missing && (
              <a href={`/projects/${projectId}?tab=frs`} target="_blank" rel="noreferrer"
                 className="h-8 px-2 rounded-md border inline-flex items-center gap-1 text-xs font-medium hover:bg-neutral-50"
                 data-testid="unlinked-add-frs"><ExternalLink className="w-3 h-3" /> Add room {row.add_to_frs} to the FRS</a>
            )}
            <button type="button" disabled={busy}
                    onClick={() => act(() => drw.post(`/sets/${setId}/link-exclude`, { on: kind, value }),
                                       `${value} excluded as a wrong takeoff`)}
                    className="h-8 px-2 rounded-md border border-rose-300 text-rose-800 inline-flex items-center gap-1 text-xs font-medium hover:bg-rose-50"
                    data-testid="unlinked-exclude"><Ban className="w-3 h-3" /> Wrong takeoff — exclude</button>
          </div>
          {row?.frs_room_missing && (
            <div className="text-[11px] text-neutral-500">Add the room and this mark links itself — no re-upload.</div>
          )}
        </div>
      )}
    </div>
  );
}
